import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Grid, TextField, Button, Typography, Divider,
  IconButton, CircularProgress, Alert, Paper, List, ListItem,
  ListItemText, ListItemSecondaryAction, InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { search as searchCollections, createPathway, updatePathway, get as getCollection } from '../../../../services/practice-collection-service';
import ElevatedContentCard from '../../../../components/containers/ElevatedContentCard';
import MarginBottomContainer from '../../../../components/containers/MarginBottomContainer';

const CreatePathwayOrCollection = ({ pathway, onCancel, onContentGenerated }) => {
  const [pathwayName, setPathwayName] = useState('');
  const [pathwayCode, setPathwayCode] = useState('');
  const [pathwayDescription, setPathwayDescription] = useState('');
  const [pathwayImageUrl, setPathwayImageUrl] = useState('');
  
  // Search collections state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState([]);
  
  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (pathway) {
      setPathwayName(pathway.name || '');
      setPathwayCode(pathway.code || '');
      setPathwayDescription(pathway.description || '');
      setPathwayImageUrl(pathway.imageUrl || '');
      
      const fetchPathwayCollections = async () => {
        if (pathway.collections && pathway.collections.length > 0) {
          try {
            const details = await Promise.all(
              pathway.collections.map(async (id) => {
                try {
                  const data = await getCollection(id);
                  return {
                    id: data.id,
                    title: data.title,
                    type: data.type,
                    schoolLevel: data.schoolLevel
                  };
                } catch (e) {
                  return {
                    id: id,
                    title: `Collection ID: ${id}`,
                    type: 'Unknown',
                    schoolLevel: 'Unknown'
                  };
                }
              })
            );
            setSelectedCollections(details);
          } catch (error) {
            console.error('Error fetching pathway collections:', error);
          }
        } else {
          setSelectedCollections([]);
        }
      };
      
      fetchPathwayCollections();
    } else {
      setPathwayName('');
      setPathwayCode('');
      setPathwayDescription('');
      setPathwayImageUrl('');
      setSelectedCollections([]);
    }
  }, [pathway]);

  // Perform search of practice collections
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const result = await searchCollections({
        term: searchQuery,
        pageNumber: 0,
        pageSize: 15,
        statuses: ['PUBLISHED', 'APPROVED', 'RELEASED', 'DRAFT'],
        types: ['FICTION', 'NON_FICTION', 'PATHWAY']
      });
      setSearchResults(result.items || []);
    } catch (error) {
      console.error('Failed to search collections:', error);
      toast.error('Failed to search collections');
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  // Trigger search on Enter key press
  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const addCollection = (collection) => {
    if (selectedCollections.some(c => c.id === collection.id)) {
      toast.info('Collection is already added');
      return;
    }
    setSelectedCollections(prev => [...prev, collection]);
  };

  const removeCollection = (collectionId) => {
    setSelectedCollections(prev => prev.filter(c => c.id !== collectionId));
  };

  const handleCreatePathway = async (event) => {
    event.preventDefault();
    if (!pathwayName.trim() || !pathwayCode.trim()) {
      setSubmitError('Name and Code are required fields');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const data = {
      name: pathwayName.trim(),
      code: pathwayCode.trim().toUpperCase(),
      description: pathwayDescription.trim() || null,
      imageUrl: pathwayImageUrl.trim() || null,
      collections: selectedCollections.map(c => c.id)
    };

    try {
      let result;
      if (pathway) {
        result = await updatePathway(pathway.id, data);
        toast.success('Practice Pathway updated successfully!');
      } else {
        result = await createPathway(data);
        toast.success('Practice Pathway created successfully!');
      }
      
      // Reset form
      setPathwayName('');
      setPathwayCode('');
      setPathwayDescription('');
      setPathwayImageUrl('');
      setSelectedCollections([]);
      setSearchResults([]);
      setSearchQuery('');
      
      if (onContentGenerated) {
        onContentGenerated(result);
      }
      onCancel();
    } catch (error) {
      console.error(pathway ? 'Error updating pathway:' : 'Error creating pathway:', error);
      setSubmitError(error.message || `Failed to ${pathway ? 'update' : 'create'} pathway`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarginBottomContainer>
      <ElevatedContentCard>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2" fontWeight="600" color="primary">
            {pathway ? 'Edit Pathway' : 'Create Pathway'}
          </Typography>
          <IconButton onClick={onCancel} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="div">
            {submitError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {submitError}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Pathway details */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  id="pathwayName"
                  name="pathwayName"
                  label="Pathway Name"
                  value={pathwayName}
                  onChange={(e) => setPathwayName(e.target.value)}
                  disabled={submitting}
                  placeholder="e.g. Grade 4 Reading Pathway"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  id="pathwayCode"
                  name="pathwayCode"
                  label="Pathway Code (Unique Identifier)"
                  value={pathwayCode}
                  onChange={(e) => setPathwayCode(e.target.value)}
                  disabled={submitting}
                  placeholder="e.g. G4_READING_PATH"
                  helperText="Must be unique. Automatically converted to uppercase."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="pathwayDescription"
                  name="pathwayDescription"
                  label="Description (Optional)"
                  multiline
                  rows={2}
                  value={pathwayDescription}
                  onChange={(e) => setPathwayDescription(e.target.value)}
                  disabled={submitting}
                  placeholder="Describe the purpose and goals of this learning pathway."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="pathwayImageUrl"
                  name="pathwayImageUrl"
                  label="Image URL (Optional)"
                  value={pathwayImageUrl}
                  onChange={(e) => setPathwayImageUrl(e.target.value)}
                  disabled={submitting}
                  placeholder="e.g. https://example.com/pathway-image.png"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                  Add Reading Practice Collections
                </Typography>
              </Grid>

              {/* Interactive split layout for searching and adding collections */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: '420px', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Search Practice Collections
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by title..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchKeyPress}
                      disabled={searching || submitting}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleSearch}
                      disabled={searching || submitting || !searchQuery.trim()}
                    >
                      {searching ? <CircularProgress size={20} color="inherit" /> : 'Search'}
                    </Button>
                  </Box>

                  <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                    {searching ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : searchResults.length === 0 ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Typography color="textSecondary" variant="body2">
                          {searchQuery ? 'No collections found' : 'Type a query and press Search'}
                        </Typography>
                      </Box>
                    ) : (
                      <List dense>
                        {searchResults.map((collection) => {
                          const isAlreadySelected = selectedCollections.some(c => c.id === collection.id);
                          return (
                            <ListItem key={collection.id} divider>
                              <ListItemText
                                primary={collection.title}
                                secondary={`${collection.type} • ${collection.schoolLevel} • Status: ${collection.status}`}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  color="primary"
                                  onClick={() => addCollection(collection)}
                                  disabled={isAlreadySelected || submitting}
                                >
                                  <AddIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: '420px', display: 'flex', flexDirection: 'column', bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Selected Collections ({selectedCollections.length})
                  </Typography>

                  <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                    {selectedCollections.length === 0 ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                        <Typography color="textSecondary" variant="body2">
                          No collections selected yet. Search and add them from the left panel.
                        </Typography>
                      </Box>
                    ) : (
                      <List dense>
                        {selectedCollections.map((collection) => (
                          <ListItem key={collection.id} divider sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                            <ListItemText
                              primary={collection.title}
                              secondary={`${collection.type} • ${collection.schoolLevel}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() => removeCollection(collection.id)}
                                disabled={submitting}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
              <Button variant="outlined" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={handleCreatePathway}
                disabled={submitting || !pathwayName.trim() || !pathwayCode.trim()}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              >
                {submitting ? (pathway ? 'Updating...' : 'Creating...') : (pathway ? 'Update Pathway' : 'Create Pathway')}
              </Button>
            </Box>
          </Box>
      </ElevatedContentCard>
    </MarginBottomContainer>
  );
};

export default CreatePathwayOrCollection;
